import type { RootState, SetState } from '../store';

export type UserSlice = {
    userContext: {
        isLoggedIn: boolean;
        setIsLoggedIn: (value: boolean) => void;
        balance: number;
        setBalance: (balance: number) => void;
        accountSettings: {
            accountID: string;
            setAccountID: (id: string) => void;
            userName: string | null;
            setUserName: (name: string) => void;
            emailAddress: string | null;
            setEmailAddress: (email: string) => void;
            currencyCode: string | null;
            setCurrencyCode: (code: string) => void;
            showBalance: boolean;
            setShowBalance: (value: boolean | undefined) => void;
        };
        session: {
            lastLogin: string;
            setLastLogin: (value: string) => void;
            timezone: string;
            setTimezone: (tz: string) => void;
            sessionStartTime: string | null;
            setSessionStartTime: (v: string | null) => void;
            initCompleted: boolean;
            setInitCompleted: (v: boolean) => void;
        };
        favoriteGames: string[];
        setFavoriteGames: (fn: ((current: string[]) => string[]) | string[]) => void;
        recentGames: string[];
        setRecentGames: (fn: ((current: string[]) => string[]) | string[]) => void;
    };
    getCurrencySymbol: () => string | null;
};

export const userSlice = ({ set }: SetState<RootState>): UserSlice => ({
    userContext: {
        isLoggedIn: false,
        setIsLoggedIn: (value) =>
            set((state) => ({
                ...state,
                userContext: {
                    ...state.userContext,
                    isLoggedIn: value
                }
            })),
        balance: 0,
        setBalance: (balance: number) =>
            set((state) => ({
                ...state,
                userContext: {
                    ...state.userContext,
                    balance: balance
                }
            })),
        accountSettings: {
            accountID: '',
            setAccountID: (id: string) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        accountSettings: {
                            ...state.userContext.accountSettings,
                            accountID: id
                        }
                    }
                })),
            userName: null,
            setUserName: (name: string) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        accountSettings: {
                            ...state.userContext.accountSettings,
                            userName: name
                        }
                    }
                })),
            emailAddress: null,
            setEmailAddress: (email: string) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        accountSettings: {
                            ...state.userContext.accountSettings,
                            emailAddress: email
                        }
                    }
                })),
            currencyCode: null,
            setCurrencyCode: (code) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        accountSettings: {
                            ...state.userContext.accountSettings,
                            currencyCode: code
                        }
                    }
                })),
            showBalance: true,
            setShowBalance: (value: boolean | undefined) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        accountSettings: {
                            ...state.userContext.accountSettings,
                            showBalance: value ?? true
                        }
                    }
                }))
        },
        session: {
            lastLogin: '',
            setLastLogin: (value: string) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        session: {
                            ...state.userContext.session,
                            lastLogin: value
                        }
                    }
                })),
            timezone: 'America/New_York',
            setTimezone: (tz) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        session: {
                            ...state.userContext.accountSettings,
                            timezone: tz
                        }
                    }
                })),
            sessionStartTime: null,
            setSessionStartTime: (v) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        session: {
                            ...state.userContext.session,
                            sessionStartTime: v
                        }
                    }
                })),
            initCompleted: false,
            setInitCompleted: (v) =>
                set((state) => ({
                    ...state,
                    userContext: {
                        ...state.userContext,
                        session: {
                            ...state.userContext.session,
                            initCompleted: v
                        }
                    }
                }))
        },
        favoriteGames: [],
        setFavoriteGames: (games: string[] | ((current: string[]) => string[])) =>
            set((state) => ({
                ...state,
                userContext: {
                    ...state.userContext,
                    // Support functional updates: allow 'games' to be a function that receives the current state and returns the new one
                    favoriteGames: typeof games === 'function' ? games(state.userContext.favoriteGames) : games
                }
            })),
        recentGames: [],
        setRecentGames: (games: string[] | ((current: string[]) => string[])) =>
            set((state) => ({
                ...state,
                userContext: {
                    ...state.userContext,
                    recentGames: typeof games === 'function' ? games(state.userContext.recentGames) : games
                }
            }))
    },
    getCurrencySymbol: (): string | null => {
        return '$';
    }
});
